import { Input, Modal, Table } from "antd";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import { useState, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import useAdminDataStore, { selectCurrentGroupId, selectAllGroups } from "../../../store/adminData/adminData";
import { postUpdateCDNUtil } from "../../../helperFunctions/util";
import GripVertical from "../../../images/grip-vertical.svg";
import { db } from "../../../services/firebaseService/connection";
import { getWebsiteConfig } from "../../../services/firebaseService/endPoints/admin/website";
import DraggableBodyRow from "../content/chants/DraggableBodyRow";
import AdminContainer from "../admin-container/AdminContainer";
import ChantButton from "../../../CommonComponents/ChantButton/ChantButton";
import { ChantInput, ChantCheckbox } from "../../../CommonComponents";
import logger from "../../../utils/logger";

const LeadershipModal = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState([]);

  const moveRow = (dragIndex, hoverIndex) => {
    const newData = JSON.parse(JSON.stringify(tableData));
    newData[dragIndex].sortIndex = hoverIndex + 1;
    let sortIndexChangeStep = hoverIndex - dragIndex > 0 ? -1 : 1;
    let i = sortIndexChangeStep > 0 ? hoverIndex : dragIndex + 1;
    let finalStep = sortIndexChangeStep < 0 ? hoverIndex : dragIndex - 1;
    for (; i <= finalStep; i++) {
      newData[i].sortIndex += sortIndexChangeStep;
    }
    setTableData(newData);
  };

  useEffect(() => {
    let websiteConfig = props.websiteConfig;
    let leadershipData = [];

    if (
      websiteConfig &&
      websiteConfig.leadership &&
      websiteConfig.leadership?.leaders
    ) {
      leadershipData = websiteConfig.leadership?.leaders.map((leader) => ({
        name: leader.name || "",
        title: leader.title || "",
        image: leader.image || "",
        email: leader.email || "",
        visibleOnWeb: leader.visibleOnWeb || false,
        visibleOnEmail: leader.visibleOnEmail || false,
        sortIndex: leader.sortIndex || 0,
        id: leader.id || "",
      }));
    }

    getDocs(collection(db, "userGroup", props.groupId, "members")).then(
      (snap) => {
        let tableData = snap.docs
          .filter(
            (doc) =>
              doc.data().userRole === "admin" ||
              doc.data().userRole === "leader"
          )
          .map((doc, index) => {
            let data = doc.data();
            return {
              name: data.name || data.fullName || "",
              sortIndex: leadershipData.length + index,
              title: data.title || "",
              visibleOnWeb: false,
              visibleOnEmail: false,
              id: doc.id,
              image: data.profileImage || "",
              email: data.email || "",
            };
          });

        let leaderIds = leadershipData.map((data) => data.id);

        tableData.forEach((row) => {
          if (leaderIds.includes(row.id)) {
            let leadershipRow = leadershipData.filter(
              (tableRow) => tableRow.id === row.id
            );
            if (leadershipRow.length > 0) {
              leadershipRow[0].name = row.name || "";
              leadershipRow[0].title = row.title || "";
              leadershipRow[0].image = row.image || "";
              leadershipRow[0].email = row.email || "";
              // Ensure boolean fields have default values
              leadershipRow[0].visibleOnWeb =
                leadershipRow[0].visibleOnWeb || false;
              leadershipRow[0].visibleOnEmail =
                leadershipRow[0].visibleOnEmail || false;
              leadershipRow[0].sortIndex = leadershipRow[0].sortIndex || 0;
            }
          }
        });

        tableData = tableData.filter((row) => {
          return !leaderIds.includes(row.id);
        });

        leadershipData.push(...tableData);

        setTableData(leadershipData);
        setIsLoading(false);
      }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const components = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const columns = [
    {
      title: "Sort",
      width: 50,
      render: () => <img src={GripVertical} alt="*" />,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (name) => name,
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (title) => title,
    },
    {
      title: "Show on website?",
      dataIndex: "visibleOnWeb",
      render: (checked, record) => (
        <ChantCheckbox
          checked={checked}
          onChange={(e) => {
            let leaders = tableData;
            leaders.find((member) => member.id === record.id).visibleOnWeb =
              e.target.checked;
            setTableData([...leaders]);
          }}
        />
      ),
    },
    {
      title: "Show email?",
      dataIndex: "visibleOnEmail",
      render: (checked, record) => (
        <ChantCheckbox
          checked={checked}
          onChange={(e) => {
            let leaders = tableData;
            leaders.find((member) => member.id === record.id).visibleOnEmail =
              e.target.checked;
            setTableData([...leaders]);
          }}
        />
      ),
    },
  ];

  const dataToRender = tableData.slice().sort(
    (a, b) => a.sortIndex - b.sortIndex
  );

  return (
    <Modal
      width={1000}
      centered
      open={props.visible}
      onOk={() => {
        props.handleModalOpen();
      }}
      onCancel={() => {
        props.handleModalOpen();
      }}
      footer={[
        <ChantButton
          type="secondary"
          key="back"
          onClick={() => {
            props.handleModalOpen();
          }}
        >
          Cancel
        </ChantButton>,
        <ChantButton
          type="primary"
          key="submit"
          loading={props.leadersSaving}
          disabled={props.leadersSaving}
          onClick={async () => {
            await props.saveDetail({
              leadership: {
                ...props.websiteConfig.leadership,
                leaders: tableData,
              },
            });
            props.handleModalOpen();
          }}
        >
          Save
        </ChantButton>,
      ]}
    >
      <div style={{ width: "90%" }}>
        <p style={{ fontWeight: "bold", fontSize: 18 }}>
          Set Leader Profiles:
        </p>
        <p>
          Select leaders to appear on website. Add leadership titles (will
          also show in their app profile). Drag/drop to sort order of
          appearance. Show/hide leader email addresses.{" "}
        </p>
        <p>
          If a leader does not appear in this list, confirm their Chant
          permissions are set to admin or leader.
        </p>
        <p>Ensure all members have a profile picture.</p>

        <div style={{ height: "20rem", overflow: "auto" }}>
          <DndProvider backend={HTML5Backend}>
            <Table
              bordered
              columns={
                window.screen.width > 500 ? columns : columns.slice(0, 2)
              }
              loading={isLoading}
              components={components}
              dataSource={dataToRender}
              pagination={{
                position: ["bottomLeft"],
                defaultPageSize: 100,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
              }}
              rowKey="id"
              size="small"
              bodyStyle={{
                fontSize: window.screen.width > 500 ? "18px" : "12px",
              }}
              onRow={(_, index) => ({
                index,
                moveRow: moveRow,
              })}
            />
          </DndProvider>
        </div>
      </div>
    </Modal>
  );
};

const Leadership = () => {
  const currentGroup = useAdminDataStore(selectCurrentGroupId);
  const allGroups = useAdminDataStore(selectAllGroups);
  const adminData = allGroups[currentGroup];

  const [leadership, setLeadership] = useState({
    overview: "",
    email: "",
    leaders: [],
  });
  const [leadershipModal, setLeadershipModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [leadersSaving, setLeadersSaving] = useState(false);

  const getState = (websiteConfig) => {
    if (
      websiteConfig &&
      websiteConfig.about &&
      websiteConfig.about.leadership
    ) {
      return {
        leadership: {
          overview: websiteConfig.about.leadership.overview || "",
          email: websiteConfig.about.leadership.email || "",
          leaders: websiteConfig.about.leadership.leaders || [],
        },
      };
    }
    // Return default structure if no data exists
    return {
      leadership: {
        overview: "",
        email: "",
        leaders: [],
      },
    };
  };

  const fetchData = () => {
    getWebsiteConfig(currentGroup).then((data) => {
      const s = getState(data);
      setLeadership(s.leadership);
    });
  };

  const openLeaderSetupModal = () => {
    setLeadershipModal((prev) => !prev);
  };

  const handleInputChange = (e) => {
    let name = e.target.name;
    if (name.includes(".")) {
      let nameParts = name.split(".");
      if (nameParts[0] === "leadership") {
        setLeadership((prev) => ({ ...prev, [nameParts[1]]: e.target.value }));
      }
    }
  };

  const saveToFirebase = () => {
    const save = async () => {
      setIsSaving(true);

      // Only include the leadership data in the about object
      let about = {
        leadership: {
          overview: leadership?.overview || "",
          email: leadership?.email || "",
          leaders: leadership?.leaders || [],
        },
      };

      const gd = adminData.data;
      const identityFields = {
        groupName: gd.groupName || "",
        clubName: gd.clubName || "",
        leagueName: gd.leagueName || "",
        country: gd.country || "",
        ...(gd.clubId ? { clubId: gd.clubId } : {}),
        ...(gd.leagueId ? { leagueId: gd.leagueId } : {}),
      };
      let docRef = doc(db, "website", gd.id);
      setDoc(docRef, { about, ...identityFields }, { merge: true }).then(() => {
        fetchData();
        postUpdateCDNUtil(currentGroup);
        setIsSaving(false);
      });
    };
    save();
  };

  const handleSave = async (state) => {
    try {
      setLeadersSaving(true);

      // Update state first with the new data
      const updatedLeadership = {
        overview: "",
        email: "",
        leaders: [],
        ...leadership,
        ...state.leadership,
      };

      setLeadership(updatedLeadership);

      // Create the about object with the updated leadership data
      let about = {
        leadership: {
          overview: updatedLeadership.overview || "",
          email: updatedLeadership.email || "",
          leaders: updatedLeadership.leaders || [],
        },
      };

      const gd2 = adminData.data;
      const identityFields2 = {
        groupName: gd2.groupName || "",
        clubName: gd2.clubName || "",
        leagueName: gd2.leagueName || "",
        country: gd2.country || "",
        ...(gd2.clubId ? { clubId: gd2.clubId } : {}),
        ...(gd2.leagueId ? { leagueId: gd2.leagueId } : {}),
      };
      let docRef = doc(db, "website", gd2.id);
      await setDoc(docRef, { about, ...identityFields2 }, { merge: true });
      fetchData();
      postUpdateCDNUtil(currentGroup);
    } catch (error) {
      logger.log(error);
    } finally {
      setLeadersSaving(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  let primaryColor = "";
  if (
    adminData.data.configuration &&
    adminData.data.configuration.primaryColor
  ) {
    let { r, g, b } = adminData.data.configuration.primaryColor;
    primaryColor = `rgb(${r}, ${g}, ${b})`;
  }

  return (
    <>
      {leadershipModal && (
        <LeadershipModal
          open={leadershipModal}
          handleModalOpen={openLeaderSetupModal}
          saveDetail={handleSave}
          visible={leadershipModal}
          groupId={currentGroup}
          websiteConfig={{ leadership }}
          leadersSaving={leadersSaving}
        />
      )}
      <AdminContainer>
        <div className="chant-form-body">
        <div style={{ marginBottom: "16px" }}>
          <label>Overview</label>
          <Input.TextArea
            style={{ borderRadius: 5 }}
            value={leadership?.overview}
            maxLength={1000}
            name="leadership.overview"
            placeholder="(optional) enter brief description of leadership team / responsibilities. Displayed above leader profiles."
            tabIndex="2"
            onChange={handleInputChange}
          />
        </div>
        <div style={{ marginBottom: "16px" }}>
          <label>Manage Profiles</label>
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <ChantButton
              style={{
                height: 28,
                minWidth: 250,
                backgroundColor: primaryColor,
                borderColor: primaryColor,
              }}
              onClick={openLeaderSetupModal}
            >
              Set Leader Profiles
            </ChantButton>
            <p style={{ fontSize: 12, margin: 0 }}>
              Add leader titles, set sort order and show/hide emails. List
              includes all members with Admin and Leader roles.
            </p>
          </div>
        </div>
        <ChantInput
          label="Group Contact Email"
          value={leadership?.email}
          onChange={handleInputChange}
          name="leadership.email"
          placeholder="Enter group email"
          tabIndex="2"
        />
        </div>
        <div className="d-flex justify-content-center">
          <div className="p-2">
            <ChantButton
              style={{
                backgroundColor: primaryColor,
                borderColor: primaryColor,
                width: "200px",
              }}
              loading={isSaving}
              onClick={saveToFirebase}
            >
              Save
            </ChantButton>
          </div>
        </div>
      </AdminContainer>
    </>
  );
};

export default Leadership;
